import React from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Package,
  Shield,
  Truck,
  Globe,
  Plane,
  Phone,
  Mail,
  Clock,
  MapPin,
  CheckCircle
} from 'lucide-react';

const WhatsAppIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Services: React.FC = () => {
  const services = [
    {
      icon: <FileText />,
      title: 'Document Courier',
      description: 'Send important documents like certificates, passports, legal papers, and business documents safely to any country with fast delivery and full tracking.',
      features: [
        'Passports, visas, and travel documents',
        'Educational certificates and transcripts',
        'Legal documents and contracts',
        'Business documents and invoices',
        'Original certificates and affidavits',
        'Complete tracking and delivery confirmation'
      ],
      color: '#3B82F6',
      deliveryTime: '4-7 business days'
    },
    {
      icon: <Package />,
      title: 'Parcel Courier',
      description: 'Ship personal parcels, gifts, household items, and online purchases internationally with secure packing and affordable rates.',
      features: [
        'Personal gifts and care packages',
        'Household items and clothing',
        'Electronics and gadgets',
        'Books and stationery',
        'Online purchase forwarding',
        'Professional multi-layer packing'
      ],
      color: '#10B981',
      deliveryTime: '5-8 business days'
    },
    {
      icon: <Shield />,
      title: 'Medicine Courier',
      description: 'Reliable international medicine delivery with proper handling and temperature care. We follow all customs and pharmaceutical regulations.',
      features: [
        'Prescription medicines with valid prescription',
        'Over-the-counter medications',
        'Ayurvedic and herbal medicines',
        'Temperature-controlled handling',
        'Original sealed packaging required',
        'Compliance with destination country regulations'
      ],
      color: '#EF4444',
      deliveryTime: '5-7 business days'
    },
    {
      icon: <Truck />,
      title: 'Food Items Courier',
      description: 'Send homemade food, pickles, spices, sweets, and dry food items to your loved ones abroad with safe and hygienic packing.',
      features: [
        'Homemade pickles and preserves',
        'Indian spices and masalas',
        'Dry snacks and namkeen',
        'Sweets and mithai (non-perishable)',
        'Tea, coffee, and dry food items',
        'Hygienic packing to prevent spoilage'
      ],
      color: '#F59E0B',
      deliveryTime: '5-8 business days'
    },
    {
      icon: <Globe />,
      title: 'Commercial Shipment',
      description: 'Bulk and commercial international shipments for businesses. Complete customs documentation, clearance support, and competitive B2B rates.',
      features: [
        'B2B bulk shipments',
        'Export documentation assistance',
        'Customs clearance support',
        'Commercial invoices and packing lists',
        'Competitive B2B pricing',
        'Dedicated account manager'
      ],
      color: '#8B5CF6',
      deliveryTime: '5-10 business days'
    },
    {
      icon: <Plane />,
      title: 'Excess Baggage',
      description: 'Relocating abroad? Ship your excess baggage and personal belongings at much lower rates than airline excess baggage charges.',
      features: [
        'Personal belongings and luggage',
        'Clothing and accessories',
        'Books and personal items',
        'Much cheaper than airline excess baggage',
        'Door-to-door pickup and delivery',
        'Special rates for students and families'
      ],
      color: '#06B6D4',
      deliveryTime: '7-12 business days'
    }
  ];

  const countries = [
    { flag: 'https://flagcdn.com/w160/us.png', name: 'USA', delivery: '5-7 Days' },
    { flag: 'https://flagcdn.com/w160/ca.png', name: 'Canada', delivery: '5-7 Days' },
    { flag: 'https://flagcdn.com/w160/gb.png', name: 'United Kingdom', delivery: '4-6 Days' },
    { flag: 'https://flagcdn.com/w160/au.png', name: 'Australia', delivery: '5-8 Days' },
    { flag: 'https://flagcdn.com/w160/ae.png', name: 'Dubai / UAE', delivery: '3-5 Days' },
    { flag: 'https://flagcdn.com/w160/eu.png', name: 'Europe', delivery: '5-7 Days' }
  ];

  const serviceAreas = [
    'Chandigarh', 'Mohali', 'Panchkula', 'Zirakpur', 'Kharar', 'Dera Bassi', 'Manimajra'
  ];

  return (
    <div className="services-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1>Our International Courier Services</h1>
            <p>Fast, safe, and affordable shipping from Chandigarh to worldwide destinations</p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="section-header">
            <h2>International Courier Services</h2>
            <p>Comprehensive shipping solutions for all your international courier needs</p>
          </motion.div>

          <div className="svc-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="svc-card"
              >
                {/* Row 1: Icon */}
                <div className="svc-icon" style={{ background: service.color }}>{service.icon}</div>

                {/* Row 2: Title */}
                <h3 className="svc-title">{service.title}</h3>

                {/* Row 3: Description */}
                <p className="svc-desc">{service.description}</p>

                {/* Row 4: Delivery badge */}
                <div className="svc-delivery">
                  <Clock size={14} />
                  <span>Delivery: {service.deliveryTime}</span>
                </div>

                {/* Row 5: Features heading + list */}
                <div className="svc-features">
                  <h4>What We Ship:</h4>
                  <ul>
                    {service.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>

                {/* Row 6: Buttons */}
                <div className="svc-buttons">
                  <a href="tel:+918847428801" className="btn btn-primary">
                    Get Quote <Phone size={16} />
                  </a>
                  <a
                    href={`https://wa.me/918847428801?text=${encodeURIComponent(`Hi, I need a quote for ${service.title} from Chandigarh.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-whatsapp"
                  >
                    WhatsApp <WhatsAppIcon size={16} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Countries */}
      <section className="countries-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="section-header">
            <h2>Countries We Serve</h2>
            <p>Reliable international courier delivery to major destinations worldwide</p>
          </motion.div>
          <div className="countries-grid">
            {countries.map((c, i) => (
              <motion.div key={c.name} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="country-card">
                <img src={c.flag} alt={`${c.name} flag`} className="country-flag" />
                <h3>{c.name}</h3>
                <p className="country-delivery"><Clock size={14} />{c.delivery}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="service-areas-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="section-header">
            <h2>Free Doorstep Pickup Areas</h2>
            <p>We provide free pickup from these locations in the Chandigarh tri-city area</p>
          </motion.div>
          <div className="service-areas-grid">
            {serviceAreas.map((area, i) => (
              <motion.div key={area} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="service-area-card">
                <MapPin size={20} />
                <h3>{area}</h3>
                <p>Free doorstep pickup available</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-services-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="section-header">
            <h2>Why Choose Pack N Fly</h2>
            <p>What makes us the best choice for international courier from Chandigarh</p>
          </motion.div>
          <div className="why-services-grid">
            {[
              { title: 'Competitive Rates', desc: 'Best prices for international courier from Chandigarh. No hidden charges, transparent pricing.' },
              { title: 'Fast Delivery', desc: 'Express and standard options. Most shipments delivered within 4-7 business days worldwide.' },
              { title: 'Safe Packing', desc: 'Professional multi-layer packing ensures your items reach safely. Special care for fragile items.' },
              { title: 'Customs Support', desc: 'Our team handles all customs documentation and clearance. No hassle for you.' },
              { title: 'Free Pickup', desc: 'Free doorstep pickup from Chandigarh, Mohali, Panchkula, and Zirakpur. No need to visit office.' },
              { title: '24/7 Tracking', desc: 'Real-time tracking for all shipments. Get updates via WhatsApp and SMS.' }
            ].map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="why-service-item">
                <CheckCircle size={24} />
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="cta-content">
            <h2>Ready to Ship Internationally?</h2>
            <p>Get the best international courier rates from Chandigarh. Call or WhatsApp us now!</p>
            <div className="cta-buttons">
              <a href="tel:+918847428801" className="btn btn-primary"><Phone size={18} /> Call: +91 8847428801</a>
              <a href="https://wa.me/918847428801?text=Hi%2C%20I%20need%20a%20quote%20for%20international%20courier%20from%20Chandigarh." target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp"><WhatsAppIcon size={18} /> WhatsApp Now</a>
              <a href="mailto:packnfly96@gmail.com" className="btn btn-secondary"><Mail size={18} /> Send Email</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
